import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check, CircleHelp, Wifi, ShieldCheck, Smartphone, Zap, CreditCard, Cog, ArrowRight,
} from "lucide-react";
import SEOHead from "@/components/SEOHead";
import RelatedReviews from "@/components/RelatedReviews";
import { useProductViewed } from "@/utils/trackingHooks";
import { fadeUp, stagger, viewportOnce, viewportOnceSmall } from "@/utils/motionVariants";
import { CARD_MACHINE_PRODUCTS } from "@/data/cardMachineProducts";
import { buildProductPageSchema } from "@/lib/schema/productPageSchema";
import ProductHero from "@/components/product-pages/ProductHero";
import QuickAnswerBlock from "@/components/product-pages/QuickAnswerBlock";
import ProductAtAGlance from "@/components/product-pages/ProductAtAGlance";
import ProductComparisonMatrix from "@/components/product-pages/ProductComparisonMatrix";
import ProductFAQSection from "@/components/product-pages/ProductFAQSection";
import RelatedProductLinks from "@/components/product-pages/RelatedProductLinks";
import ProductFinalCTA from "@/components/product-pages/ProductFinalCTA";

/** Short trust ribbon straight after the hero — mirrors the hub page strip. */
function TrustRibbon({ slug }) {
  return (
    <section className="py-8 bg-white border-b border-brand-light-grey/50" data-testid={`${slug}-trust-ribbon`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Wifi, label: "Reliable Connectivity" },
            { icon: ShieldCheck, label: "PCI Compliant" },
            { icon: Smartphone, label: "Contactless Ready" },
            { icon: Zap, label: "Next-Day Setup" },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-3 p-3.5 rounded-xl bg-brand-aqua-light">
              <item.icon size={18} className="text-brand-aqua flex-shrink-0" aria-hidden="true" />
              <span className="text-sm font-semibold text-brand-black">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BestForSection({ product }) {
  return (
    <section className="py-16 md:py-20 bg-white" data-testid={`${product.slug}-best-for`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-8">
          {`Who is the ${product.shortName} best for?`}
        </h2>
        <motion.ul
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnceSmall}
          variants={stagger}
          className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4"
        >
          {product.bestFor.map((item) => (
            <motion.li key={item} variants={fadeUp} className="flex items-start gap-3 text-sm text-brand-grey leading-relaxed">
              <Check size={16} className="text-brand-aqua mt-0.5 flex-shrink-0" aria-hidden="true" />
              {item}
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
}

function WhyChooseSection({ product }) {
  return (
    <section className="py-16 md:py-20 bg-gray-50" data-testid={`${product.slug}-why-choose`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-8 text-center">
          {`Why choose the ${product.name}?`}
        </h2>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnceSmall}
          variants={stagger}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {product.whyChoose.map((reason) => (
            <motion.div key={reason.title} variants={fadeUp} className="bg-white border border-brand-light-grey rounded-2xl p-6">
              <h3 className="font-heading text-lg font-bold text-brand-black mb-3">{reason.title}</h3>
              <p className="text-sm text-brand-grey leading-relaxed">{reason.body}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function EnvironmentSection({ product }) {
  const env = product.environment;
  return (
    <section className="py-16 md:py-20 bg-white" data-testid={`${product.slug}-environment`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={fadeUp}>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-6">{env.heading}</h2>
            <div className="space-y-4 text-sm leading-relaxed text-brand-grey">
              {env.paragraphs.map((p) => (
                <p key={p.slice(0, 32)}>{p}</p>
              ))}
            </div>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={fadeUp} className="space-y-4">
            <div className="bg-white border border-brand-light-grey rounded-2xl p-4 flex items-center justify-center">
              <img src={env.image} alt={env.imageAlt} className="max-h-72 w-auto object-contain rounded-xl" loading="lazy" />
            </div>
            {product.gallery?.length > 0 && (
              <div className="grid grid-cols-3 gap-4">
                {product.gallery.map((g) => (
                  <div key={g.src} className="bg-white border border-brand-light-grey rounded-xl overflow-hidden flex items-center justify-center aspect-square">
                    <img src={g.src} alt={g.alt} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function CapabilitiesSection({ product }) {
  return (
    <section className="py-16 md:py-20 bg-gray-50" data-testid={`${product.slug}-capabilities`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-brand-aqua-light flex items-center justify-center">
                <CreditCard size={17} className="text-brand-aqua" aria-hidden="true" />
              </div>
              <h2 className="font-heading text-xl font-bold text-brand-black">Payment methods & capabilities</h2>
            </div>
            <ul className="space-y-3">
              {product.capabilities.map((c) => (
                <li key={c} className="flex items-start gap-3 text-sm text-brand-grey">
                  <Check size={15} className="text-brand-aqua mt-0.5 flex-shrink-0" aria-hidden="true" />
                  {c}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-brand-aqua-light flex items-center justify-center">
                <Cog size={17} className="text-brand-aqua" aria-hidden="true" />
              </div>
              <h2 className="font-heading text-xl font-bold text-brand-black">Technical highlights</h2>
            </div>
            <ul className="space-y-3">
              {product.technicalHighlights.map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-brand-grey">
                  <Check size={15} className="text-brand-aqua mt-0.5 flex-shrink-0" aria-hidden="true" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function IsRightForSection({ product }) {
  return (
    <section className="py-16 md:py-20 bg-white" data-testid={`${product.slug}-is-right-for`}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-8 text-center">
          {`Is the ${product.shortName} right for your business?`}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-brand-aqua-light/60 border border-brand-aqua/20 rounded-2xl p-6">
            <h3 className="font-heading text-base font-bold text-brand-black mb-4 flex items-center gap-2">
              <Check size={17} className="text-brand-aqua" aria-hidden="true" /> A strong fit if…
            </h3>
            <ul className="space-y-3">
              {product.isRightFor.yes.map((item) => (
                <li key={item} className="text-sm text-brand-grey leading-relaxed">{item}</li>
              ))}
            </ul>
          </div>
          <div className="bg-gray-50 border border-brand-light-grey rounded-2xl p-6">
            <h3 className="font-heading text-base font-bold text-brand-black mb-4 flex items-center gap-2">
              <CircleHelp size={17} className="text-brand-grey" aria-hidden="true" /> Worth comparing if…
            </h3>
            <ul className="space-y-3">
              {product.isRightFor.consider.map((item) => (
                <li key={item} className="text-sm text-brand-grey leading-relaxed">{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyCanDoSection({ slug }) {
  return (
    <section className="py-16 md:py-20 bg-gray-50" data-testid={`${slug}-why-cando`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-4">Why get it from CanDo?</h2>
        <p className="text-sm leading-relaxed text-brand-grey mb-4 max-w-2xl mx-auto">
          The hardware is only half the decision — the merchant account behind it decides what you actually pay and who answers when something breaks. CanDo is a family-run, B Corp certified payments provider: transparent pricing agreed up front, onboarding handled for you, and a UK-based team you can actually ring.
        </p>
        <p className="text-sm leading-relaxed text-brand-grey mb-6 max-w-2xl mx-auto">
          Already taking card payments? We'll review your statement and show you exactly what switching would save before you commit.
        </p>
        <Link
          to="/statement-review"
          className="inline-flex items-center gap-2 text-sm font-semibold text-brand-aqua hover:text-brand-aqua-dark transition-colors"
          data-testid={`${slug}-statement-review-link`}
        >
          Get a free statement review <ArrowRight size={15} />
        </Link>
      </div>
    </section>
  );
}

export default function CardMachineProductPage() {
  const { product: slug } = useParams();
  const product = CARD_MACHINE_PRODUCTS[slug];
  useProductViewed(slug ? `card_machine_${slug.replace(/-/g, "_")}` : "card_machine_unknown");
  if (!product) return <Navigate to="/card-machines" replace />;

  const path = `/card-machines/${product.slug}`;

  return (
    <div>
      <SEOHead
        rawTitle={product.metaTitle}
        description={product.metaDescription}
        path={path}
        ogTitle={product.ogTitle}
        ogDescription={product.ogDescription}
        ogImage={product.ogImage || undefined}
        ogImageAlt={product.ogImage ? product.heroImageAlt : undefined}
        faqs={product.faqs}
        schema={buildProductPageSchema(product)}
      />
      <ProductHero product={product} />
      <TrustRibbon slug={product.slug} />
      <QuickAnswerBlock
        heading={product.quickAnswer.heading}
        answer={product.quickAnswer.answer}
        supporting={product.quickAnswer.supporting}
        testId={`${product.slug}-quick-answer`}
      />
      <ProductAtAGlance product={product} />
      <BestForSection product={product} />
      <WhyChooseSection product={product} />
      <EnvironmentSection product={product} />
      <ProductComparisonMatrix currentSlug={product.slug} testId={`${product.slug}-comparison`} />
      <CapabilitiesSection product={product} />
      <IsRightForSection product={product} />
      <WhyCanDoSection slug={product.slug} />
      <ProductFAQSection product={product} />
      <RelatedProductLinks product={product} />
      <ProductFinalCTA product={product} />
      <RelatedReviews
        product="card-machines"
        eyebrow="Customer Stories"
        heading={`What UK businesses say about CanDo card machines`}
        subheading="Real reviews from businesses taking payments with CanDo every day."
        testId={`${product.slug}-related-reviews`}
      />
    </div>
  );
}
